import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { contractsAPI } from '../services/api'
import type { Contract } from '../types'

export default function Dashboard() {
  const { t } = useTranslation()
  const [contracts, setContracts] = useState<Contract[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    contractsAPI.list().then((resp) => {
      setContracts(resp.data.contracts)
    }).catch(() => {}).finally(() => setLoading(false))
  }, [])

  const statusCounts = {
    draft: contracts.filter(c => c.status === 'draft').length,
    review: contracts.filter(c => c.status === 'review').length,
    approved: contracts.filter(c => c.status === 'approved').length,
    published: contracts.filter(c => c.status === 'published').length,
    signed: contracts.filter(c => c.status === 'signed').length,
  }

  return (
    <div>
      <div className="page-header">
        <h1><i className="bi bi-speedometer2 me-2"></i>{t('nav.dashboard')}</h1>
        <Link to="/contracts/new" className="btn btn-primary">
          <i className="bi bi-plus-lg me-1"></i>
          {t('contract.new')}
        </Link>
      </div>

      {/* Stats */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card border-primary">
            <div className="card-body text-center py-3">
              <div className="display-4 fw-bold text-primary">{statusCounts.draft}</div>
              <div className="text-muted small text-uppercase fw-semibold">{t('contract.status.draft')}</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-warning">
            <div className="card-body text-center py-3">
              <div className="display-4 fw-bold text-warning">{statusCounts.review}</div>
              <div className="text-muted small text-uppercase fw-semibold">{t('contract.status.review')}</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-info">
            <div className="card-body text-center py-3">
              <div className="display-4 fw-bold text-info">{statusCounts.approved}</div>
              <div className="text-muted small text-uppercase fw-semibold">{t('contract.status.approved')}</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-success">
            <div className="card-body text-center py-3">
              <div className="display-4 fw-bold text-success">{statusCounts.signed}</div>
              <div className="text-muted small text-uppercase fw-semibold">{t('contract.status.signed')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent contracts */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span>{t('contract.title')}</span>
          <Link to="/contracts" className="btn btn-sm btn-outline-primary">
            <i className="bi bi-arrow-right"></i>
          </Link>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status" />
            </div>
          ) : contracts.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-file-earmark-text display-4 d-block mb-2"></i>
              <p>No hay contratos todavía</p>
              <Link to="/contracts/new" className="btn btn-primary btn-sm">
                <i className="bi bi-plus-lg"></i> {t('contract.new')}
              </Link>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>{t('contract.number')}</th>
                    <th>{t('contract.title')}</th>
                    <th>{t('contract.client')}</th>
                    <th>{t('contract.status')}</th>
                    <th>{t('contract.price')}</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.slice(0, 5).map((c) => (
                    <tr key={c.id}>
                      <td className="fw-semibold">{c.contract_number}</td>
                      <td>
                        <Link to={`/contracts/${c.id}`} className="text-decoration-none">
                          {c.title}
                        </Link>
                      </td>
                      <td>{c.client_company_name || '—'}</td>
                      <td>
                        <span className={`badge bg-${statusBadge(c.status)}`}>
                          {t(`contract.status.${c.status}`)}
                        </span>
                      </td>
                      <td>{c.price_amount ? `${c.price_amount.toLocaleString()} €` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function statusBadge(status: string): string {
  const map: Record<string, string> = {
    draft: 'secondary',
    review: 'warning',
    approved: 'info',
    published: 'primary',
    signed: 'success',
    expired: 'dark',
    cancelled: 'danger',
  }
  return map[status] || 'secondary'
}
